import CardAguardando from './CardAguardando';
import LinhaPresenca from './LinhaPresenca';
import { ehCancelada } from '../presencaFormacao';
import type { InscritoPresenca } from '../types';

interface Props {
  linhas: InscritoPresenca[];
  salvandoManual: boolean;
  avaliandoId: number | null;
  liberandoId: number | null;
  aoAvaliar: (presencaId: number, aprovar: boolean, obs?: string) => void;
  aoLiberarReenvio: (presencaId: number) => void;
  aoMarcar: (inscricaoId: number, obs: string) => void;
  aoAbrirHistorico: (inscricaoId: number, nome: string) => void;
  aoAmpliar: (url: string | null) => void;
  formacaoQuando?: string | null;
  aoCancelar?: (linha: InscritoPresenca) => void;
  aoDesfazer?: (linha: InscritoPresenca) => void;
  desfazendoId?: number | null;
}

function CabecalhoSecao({ titulo, total, cor }: { titulo: string; total: number; cor: string }) {
  return (
    <div className="flex items-center gap-2">
      <h4 className="text-sm font-semibold text-foreground-900">{titulo}</h4>
      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cor}`}>{total}</span>
    </div>
  );
}

export default function ListaTodosPresenca({
  linhas,
  salvandoManual,
  avaliandoId,
  liberandoId,
  aoAvaliar,
  aoLiberarReenvio,
  aoMarcar,
  aoAbrirHistorico,
  aoAmpliar,
  formacaoQuando,
  aoCancelar,
  aoDesfazer,
  desfazendoId,
}: Props) {
  const faltas = linhas.filter((l) => l.situacao === 'falta');
  const aguardando = linhas.filter((l) => l.situacao === 'aguardando');
  const canceladas = linhas.filter((l) => ehCancelada(l));
  const recusadas = linhas.filter((l) => l.situacao === 'recusada' && !ehCancelada(l));
  const confirmadas = linhas.filter((l) => l.situacao === 'confirmada');

  if (linhas.length === 0) {
    return <p className="text-sm text-secondary-500">Nenhuma pessoa encontrada.</p>;
  }

  return (
    <div className="space-y-8">
      {faltas.length > 0 && (
        <section>
          <CabecalhoSecao
            titulo="Ainda não abriram"
            total={faltas.length}
            cor="bg-secondary-100 text-secondary-600"
          />
          <p className="mt-1 text-xs text-secondary-500">
            Quem ainda não marcou presença. Marque na mão pra quem não conseguiu entrar.
          </p>
          <div className="mt-3 overflow-hidden rounded-3xl bg-white shadow-[0_6px_18px_rgba(20,25,60,.06)]">
            {faltas.map((l, i) => (
              <div key={l.inscricao_id} className={i > 0 ? 'border-t border-secondary-100' : ''}>
                <LinhaPresenca
                  linha={l}
                  tipo="falta"
                  salvando={salvandoManual}
                  liberando={liberandoId === l.presenca_id}
                  aoMarcar={aoMarcar}
                  aoLiberarReenvio={aoLiberarReenvio}
                  formacaoQuando={formacaoQuando}
                  aoCancelar={aoCancelar}
                  aoDesfazer={aoDesfazer}
                  desfazendo={desfazendoId === l.presenca_id}
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {aguardando.length > 0 && (
        <section>
          <CabecalhoSecao
            titulo="Aguardando você"
            total={aguardando.length}
            cor="bg-amber-100 text-amber-700"
          />
          <p className="mt-1 text-xs text-secondary-500">
            Fotos enviadas esperando sua aprovação.
          </p>
          <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
            {aguardando.map((l) => (
              <CardAguardando
                key={l.inscricao_id}
                linha={l}
                avaliando={avaliandoId === l.presenca_id}
                liberando={liberandoId === l.presenca_id}
                aoConfirmar={() => aoAvaliar(l.presenca_id as number, true)}
                aoRecusar={(motivo) => aoAvaliar(l.presenca_id as number, false, motivo)}
                aoLiberarReenvio={() => aoLiberarReenvio(l.presenca_id as number)}
                aoAbrirHistorico={() => aoAbrirHistorico(l.inscricao_id, l.nome)}
                aoAmpliar={(url) => aoAmpliar(url)}
              />
            ))}
          </div>
        </section>
      )}

      {recusadas.length > 0 && (
        <section>
          <CabecalhoSecao
            titulo="Recusadas"
            total={recusadas.length}
            cor="bg-red-100 text-red-700"
          />
          <p className="mt-1 text-xs text-secondary-500">
            Foto recusada. Você pode marcar presença na mão ou liberar nova tentativa.
          </p>
          <div className="mt-3 overflow-hidden rounded-3xl bg-white shadow-[0_6px_18px_rgba(20,25,60,.06)]">
            {recusadas.map((l, i) => (
              <div key={l.inscricao_id} className={i > 0 ? 'border-t border-secondary-100' : ''}>
                <LinhaPresenca
                  linha={l}
                  tipo="recusada"
                  salvando={salvandoManual}
                  liberando={liberandoId === l.presenca_id}
                  aoMarcar={aoMarcar}
                  aoLiberarReenvio={aoLiberarReenvio}
                  formacaoQuando={formacaoQuando}
                  aoCancelar={aoCancelar}
                  aoDesfazer={aoDesfazer}
                  desfazendo={desfazendoId === l.presenca_id}
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {canceladas.length > 0 && (
        <section>
          <CabecalhoSecao
            titulo="Canceladas"
            total={canceladas.length}
            cor="bg-secondary-100 text-secondary-600"
          />
          <p className="mt-1 text-xs text-secondary-500">
            Presenças canceladas pela direção. Dá para desfazer.
          </p>
          <div className="mt-3 overflow-hidden rounded-3xl bg-white shadow-[0_6px_18px_rgba(20,25,60,.06)]">
            {canceladas.map((l, i) => (
              <div key={l.inscricao_id} className={i > 0 ? 'border-t border-secondary-100' : ''}>
                <LinhaPresenca
                  linha={l}
                  tipo="recusada"
                  salvando={salvandoManual}
                  liberando={liberandoId === l.presenca_id}
                  aoMarcar={aoMarcar}
                  aoLiberarReenvio={aoLiberarReenvio}
                  formacaoQuando={formacaoQuando}
                  aoCancelar={aoCancelar}
                  aoDesfazer={aoDesfazer}
                  desfazendo={desfazendoId === l.presenca_id}
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {confirmadas.length > 0 && (
        <section>
          <CabecalhoSecao
            titulo="Confirmadas"
            total={confirmadas.length}
            cor="bg-green-100 text-green-700"
          />
          <div className="mt-3 overflow-hidden rounded-3xl bg-white shadow-[0_6px_18px_rgba(20,25,60,.06)]">
            {confirmadas.map((l, i) => (
              <div key={l.inscricao_id} className={i > 0 ? 'border-t border-secondary-100' : ''}>
                <LinhaPresenca
                  linha={l}
                  tipo="presente"
                  salvando={salvandoManual}
                  liberando={false}
                  aoMarcar={aoMarcar}
                  aoLiberarReenvio={aoLiberarReenvio}
                  formacaoQuando={formacaoQuando}
                  aoCancelar={aoCancelar}
                  aoDesfazer={aoDesfazer}
                  desfazendo={desfazendoId === l.presenca_id}
                />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}